import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import makeStyles from "@material-ui/styles/makeStyles";
import { useSnackbar } from "notistack";
import React from "react";
import { useGetModuleExamDetailsQuery } from "../../../../api/rtk/SignUpApi";
import { ModuleSelectionViewModel } from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import TranslatedMessage from "../../../translations/TranslatedMessage";
import { useSignUpContext } from "../../CertificationDataWizardContext/CertificationDataWizardContext";
import { ControlButtons } from "../shared/ControlButtons";
import ExamLocationAndDateStepRow from "./ExamLocationAndDateStepRow";
import { ExamType } from "../../../../model/Exam";
import Alert from "@material-ui/lab/Alert/Alert";
import theme from "../../../../theme";

const useStyles = makeStyles({
  description: {
    paddingTop: 20,
    paddingBottom: 20,
  },
});

type ExamLocationModel = {
  examId: string;
  sessionId: string;
  day: string;
  locationId: string;
};

const ExamLocationAndDateStep: React.FunctionComponent = () => {
  const {
    data: { modulesSelection: initialModulesSelection },
    setValues,
    handleNextStep,
    handlePrevStep,
    refetch: refetchAllModules,
  } = useSignUpContext();
  const [modulesSelection, setModulesSelection] = React.useState(initialModulesSelection);
  const [fetched, setFetched] = React.useState(false);
  const [isFullyBookedChecked, setIsFullyBookedChecked] = React.useState(false);
  const {
    data: allExamPossibleDetails,
    isFetching,
    refetch,
  } = useGetModuleExamDetailsQuery(modulesSelection, {
    skip: fetched,
  });
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFullyBookedForCurrentSelection = (currentSelection: ModuleSelectionViewModel[]) => {
    const newSelection = currentSelection.map((s) => {
      return {
        ...s,
        examDetails: s.examDetails?.map((examDetails) => {
          var isFullyBooked = allExamPossibleDetails?.some(
            (details) =>
              details.examSessionId === s.examSessionId &&
              details.courseId === s.courseId &&
              details.moduleId === s.moduleId &&
              details.examId === examDetails.examId &&
              details.language === examDetails.language &&
              details.availableLocations.length === 0
          );
          return { ...examDetails, isFullyBooked: isFullyBooked };
        }),
      };
    });
    return newSelection;
  };

  React.useEffect(() => {
    if (!isFetching && allExamPossibleDetails) {
      setFetched(true);
      if (!isFullyBookedChecked) {
        setIsFullyBookedChecked(true);
        setModulesSelection(setFullyBookedForCurrentSelection(modulesSelection));
      }
    }
  }, [allExamPossibleDetails, isFetching, isFullyBookedChecked, modulesSelection, setFullyBookedForCurrentSelection]);

  React.useEffect(() => {
    setIsFullyBookedChecked(false);
    setFetched(false);
  }, [refetch]);

  React.useEffect(() => {
    if (!fetched) {
      refetch();
      refetchAllModules();
      setIsFullyBookedChecked(false);
    }
  }, [fetched, refetch, refetchAllModules]);

  const updateModuleSelection = (update: ModuleSelectionViewModel) => {
    const index = modulesSelection.findIndex(
      (m) =>
        m.courseId === update.courseId && m.examSessionId === update.examSessionId && m.moduleId === update.moduleId
    );
    if (index >= 0) {
      setModulesSelection([...modulesSelection.slice(0, index), update, ...modulesSelection.slice(index + 1)]);
    }
  };

  const examsHaveSameLocation = (): boolean => {
    const examsLocationDays = modulesSelection.reduce(
      (allExamsLocationsDays: ExamLocationModel[], module: ModuleSelectionViewModel) => {
        const examsLocations: ExamLocationModel[] = (module.examDetails ?? [])
          .map((exam) => {
            return {
              examId: exam.examId,
              sessionId: module.examSessionId,
              day: exam.examDayDate,
              locationId: exam.locationId,
            };
          })
          .filter((exam): exam is ExamLocationModel => {
            return !!exam.day && !!exam.locationId;
          });

        allExamsLocationsDays.push(...examsLocations);
        return allExamsLocationsDays;
      },
      [] as ExamLocationModel[]
    );

    for (const locationDay of examsLocationDays) {
      if (
        examsLocationDays.some(
          (item) =>
            item.examId !== locationDay.examId &&
            item.sessionId === locationDay.sessionId &&
            item.day === locationDay.day &&
            item.locationId !== locationDay.locationId
        )
      ) {
        return false;
      }
    }
    return true;
  };

  const onNextStep = () => {
    if (!examsHaveSameLocation()) {
      enqueueSnackbar(
        <TranslatedMessage id="application.examLocationAndDate.validattion.examsHaveDifferentLocations" />
      );
      return;
    }

    setValues({ modulesSelection });
    handleNextStep();
  };

  const onPreviousStep = () => {
    setValues({ modulesSelection });
    handlePrevStep();
  };

  const isSelectionComplete = (): boolean => {
    if (!allExamPossibleDetails) {
      return false;
    }

    for (const module of modulesSelection) {
      const currentValidSelection = (module.examDetails ?? []).filter(
        (details) =>
          details.locationId !== undefined &&
          details.examDayId !== undefined &&
          details.examType !== ExamType.TermPaper &&
          !details.isFullyBooked
      );

      const expectedSelection =
        module.examDetails
          ?.filter((ed) => ed.examType !== ExamType.TermPaper)
          .map((selection) => {
            return allExamPossibleDetails?.find(
              (details) =>
                details.examSessionId === module.examSessionId &&
                details.courseId === module.courseId &&
                details.moduleId === module.moduleId &&
                details.examId === selection.examId &&
                (details.language === selection.language || !details.noLocationRequired)
            );
          }) ?? [];
      if (expectedSelection?.length !== currentValidSelection.length) {
        return false;
      }
    }
    return true;
  };

  if (isFetching || !allExamPossibleDetails || !fetched) {
    return (
      <>
        <LinearProgress variant="query" />
        <Alert severity="info" style={{ marginTop: theme.spacing(2), padding: theme.spacing(2) }}>
          <TranslatedMessage id="application.examLocationAndDate.chkeckingAvailabilityOfExams" />
        </Alert>
      </>
    );
  }

  return (
    <>
      <Typography variant="h6">
        <TranslatedMessage id="application.examLocationAndDateExplanationText" />
      </Typography>
      <Typography className={classes.description}>
        <TranslatedMessage id="application.selectedExamsTitle" />
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TranslatedMessage id="application.examLocationAndDate.moduleColumn" />
              </TableCell>
              <TableCell>
                <TranslatedMessage id="application.examLocationAndDate.examColumn" />
              </TableCell>
              <TableCell>
                <TranslatedMessage id="application.examLocationAndDate.examTypeColumn" />
              </TableCell>
              <TableCell>
                <TranslatedMessage id="application.examLocationAndDate.languageColumn" />
              </TableCell>
              <TableCell>
                <TranslatedMessage id="application.examLocationAndDate.locationColumn" />
              </TableCell>
              <TableCell>
                <TranslatedMessage id="application.examLocationAndDate.dateColumn" />
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {modulesSelection.map((m, i) => (
              <ExamLocationAndDateStepRow
                key={i}
                module={m}
                updateModuleSelection={updateModuleSelection}
                allExamPossibleDetails={allExamPossibleDetails ?? []}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ControlButtons
        onNextClick={onNextStep}
        onPreviousClick={onPreviousStep}
        nextDisabled={isFetching || !isSelectionComplete()}
      />
    </>
  );
};

export default ExamLocationAndDateStep;
